/**
 * `true` once the element has been on screen (and from then on). Used by the
 * person panel's run cards so only the cards a viewer actually scrolls to read
 * their model calls: opening a tab with many runs used to fire every card's
 * `/prompt` at once, and those reads share a small file pool with the inline
 * AI Review preview (PR #1036 iter 5).
 *
 * `eager` skips the wait (the latest run). Without `IntersectionObserver`
 * (old browsers, test DOMs) every card counts as seen — the pre-lazy
 * behaviour, never a card that cannot load.
 */
import { type RefObject, useEffect, useState } from "react";

export function useSeenOnce(ref: RefObject<Element | null>, eager = false): boolean {
  const [seen, setSeen] = useState(eager || typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setSeen(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, seen]);
  return seen;
}
